import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';

/**
 * Community & Referrals (Joe), practice side. Every call names the location. The server answers
 * 404 for all of it without the `community` practice flag.
 */

export interface CommunityOption {
  key: string;
  label: string;
}

export interface CommunityState {
  featureEnabled: boolean;
  joeActive: boolean;
  /** Joe is on for the practice but paused at this location by the PracticeNova team. */
  joePaused?: boolean;
  /** Public calendars Joe found near this location, waiting for the PracticeNova team's approval. */
  calendarsProposed?: number;
  location: { id: string; name: string; city: string | null; state: string | null; timezone: string };
  settings: {
    practiceType: string;
    practiceTypeLabel: string;
    /** in: this practice needs referrals in; out: it refers out and wants community goodwill. */
    referralDirection: 'in' | 'out';
    /** One line on where this practice type's referrals come from. */
    rationale: string;
    radiusMi: number;
    radiusOptions: readonly number[];
    enabledCategories: string[];
    categoryOptions: Array<CommunityOption & { kind: 'partner' | 'nontraditional_partner' }>;
    enabledEventTypes: CommunityOption[];
    /** Every category: the practice's profile first, then the rest by group. */
    categoryGroups?: Array<{ key: string; label: string; options: Array<CommunityOption & { kind: 'partner' | 'nontraditional_partner' }> }>;
    /** Every event type, the profile's defaults first; `inProfile` marks the defaults. */
    eventTypeOptions?: Array<CommunityOption & { inProfile?: boolean }>;
    referralCheckinOn: boolean;
  };
  vocabulary: {
    touchTypes: Array<CommunityOption & { needsWords: boolean }>;
    rhythms: CommunityOption[];
    reasons: CommunityOption[];
  };
}

/** What an event's own page says about taking part: quotes and links, nothing inferred. */
export interface VendorInfo {
  pageUrl: string;
  registrationUrl: string | null;
  cost: string | null;
  deadlineText: string | null;
  deadline: string | null;
  attendance: string | null;
  pastVendors: string[];
  fetchedAt: string;
}

export interface Pick {
  id: string;
  kind: 'partner' | 'nontraditional_partner' | 'event';
  externalKey: string;
  name: string;
  org: string | null;
  category: string;
  categoryLabel: string;
  distanceMi: number;
  eventStart: string | null;
  why: string;
  suggestedTouch: string;
  suggestedTouchLabel: string;
  source: string;
  sourceUrl: string | null;
  lastVerified: string;
  score: number;
  state: string;
  hasCost: boolean;
  reasons: string[];
  weekOf: string;
  /** The registry lists it as enumerated in the last 12 months: a practice new to the area. */
  isNew?: boolean;
  /** In `reserve` only: the scan chose it this month, but Joe has not written its why line yet. */
  waitingForJoe?: boolean;
  /** Events: the vendor or booth application deadline, when the listing or event page gave one. */
  boothDeadline?: string | null;
  /** Events: what the event's own page says about vendors, when Joe read it. */
  vendorInfo?: VendorInfo | null;
}

export interface LearnedRule {
  id: string;
  scope: string;
  reason: string;
  label: string;
  isBlock: boolean;
  weight: number | null;
  createdAt: string;
}

export interface ComingUpItem {
  id: string;
  label: string;
  date: string;
}

export interface PartnerRow {
  id: string;
  name: string;
  role: string | null;
  org: string | null;
  category: string;
  categoryLabel: string;
  rhythm: string;
  rhythmDays: number | null;
  rhythmLabel: string;
  status: 'on_track' | 'due_soon' | 'overdue' | 'planned' | 'new';
  statusLabel: string;
  lastTouch: { activityType: string; label: string; date: string } | null;
  nextPlanned: { activityType: string; label: string; date: string } | null;
  nextDue: string | null;
  daysOverdue: number;
  referrals90d: number | null;
  archived: boolean;
}

export interface PicksResponse {
  joeActive: boolean;
  weekOf: string | null;
  picks: Pick[];
  refreshedAt: string | null;
  scoutStatus: string | null;
  /** The newest scan attempt of any status: running, awaiting_joe, done, failed or skipped. */
  lastScan: { status: string; at: string; note: string | null } | null;
  /** What the last scan could not search (an outage, a missing feed or key), or null. */
  sourceWarning: string | null;
  /** Every pick Joe wrote this month with its live state (new, planned, snoozed, dismissed). */
  weekPicks: Pick[];
  /** Joe's other ranked options this month (his scan's choices still waiting for a why line first);
   *  `why` holds the fact each was found by. */
  reserve: Pick[];
  overdue: PartnerRow[];
  overdueCount: number;
  learned: LearnedRule[];
  comingUp: ComingUpItem[];
}

export interface PartnerDetail extends PartnerRow {
  address: string | null;
  touches: Array<{
    id: string;
    activityType: string;
    label: string;
    status: 'planned' | 'done' | 'skipped';
    date: string;
    notes: string | null;
    draftRequested: boolean;
    draftTaskId: string | null;
  }>;
  referralCounts: Array<{ month: string; count: number | null; source: string }>;
}

export interface PlanTouchInput {
  pickId?: string;
  partnerId?: string;
  partner?: { name: string; role?: string | null; org?: string | null; category?: string; address?: string | null };
  activityType: string;
  activityOther?: string | null;
  date: string;
  rhythm?: string;
  rhythmDays?: number | null;
  notes?: string | null;
  draftRequested?: boolean;
}

export interface DismissResult {
  pick: Pick;
  rule: LearnedRule | null;
  message: string;
  /** Joe's follow-up ask after a pattern of dismissals; the page offers a one-click answer. */
  followUp: { kind: 'drop_category' | 'shrink_radius'; detail: string; category?: string } | null;
}

export interface ReferralRow {
  partnerId: string;
  name: string;
  prefillCount: number | null;
  prefillSource: 'voice_agent' | 'booking_link' | 'none';
  prefillLabel: string;
  confirmedCount: number | null;
}

export interface CadenceDue {
  partner: PartnerRow;
  suggestedTouch: string;
  overdueCount: number;
  /** The partner used to send patients and has sent none for the last two confirmed months. */
  referralDropOff?: boolean;
  /** Owner-confirmed counts, newest month first. */
  recentReferrals?: Array<{ month: string; count: number | null }>;
}

const unwrap = <T,>(res: { data: ApiResponse<T> }): T => res.data.data;

export const communityApi = {
  getState: (locationId: string) =>
    apiClient.get<ApiResponse<CommunityState>>('/community/state', { params: { locationId } }).then(unwrap),
  getPicks: (locationId: string) =>
    apiClient.get<ApiResponse<PicksResponse>>('/community/picks', { params: { locationId } }).then(unwrap),
  /** Queue a fresh scan now (at most one per 10 minutes per location); Joe writes the picks after. */
  refreshPicks: (locationId: string) =>
    apiClient.post<ApiResponse<{ queued: boolean; message: string }>>('/community/picks/refresh', { locationId }).then(unwrap),
  snoozePick: (locationId: string, pickId: string) =>
    // A second snooze on a returned pick counts as "not a fit" and comes back with the rule to undo.
    apiClient.post<ApiResponse<{ pick: Pick; message: string; rule?: LearnedRule | null }>>(`/community/picks/${pickId}/snooze`, { locationId }).then(unwrap),
  addReservePick: (locationId: string, pickId: string) =>
    apiClient.post<ApiResponse<{ pick: Pick; message: string }>>(`/community/picks/${pickId}/add`, { locationId }).then(unwrap),
  dismissPick: (locationId: string, pickId: string, reason: string) =>
    apiClient.post<ApiResponse<DismissResult>>(`/community/picks/${pickId}/dismiss`, { locationId, reason }).then(unwrap),
  undoRule: (locationId: string, ruleId: string) =>
    apiClient.post<ApiResponse<{ ok: true }>>(`/community/rules/${ruleId}/undo`, { locationId }).then(unwrap),
  addEvent: (locationId: string, input: { title: string; start: string; org?: string | null; url?: string | null }) =>
    apiClient.post<ApiResponse<Pick>>('/community/events', { locationId, ...input }).then(unwrap),
  getNetwork: (locationId: string) =>
    apiClient.get<ApiResponse<PartnerRow[]>>('/community/network', { params: { locationId } }).then(unwrap),
  getPartner: (locationId: string, partnerId: string) =>
    apiClient.get<ApiResponse<PartnerDetail>>(`/community/partners/${partnerId}`, { params: { locationId } }).then(unwrap),
  updatePartner: (
    locationId: string,
    partnerId: string,
    patch: { rhythm?: string; rhythmDays?: number | null; archived?: boolean; name?: string; role?: string | null; org?: string | null },
  ) => apiClient.patch<ApiResponse<PartnerDetail>>(`/community/partners/${partnerId}`, { locationId, ...patch }).then(unwrap),
  /** The owner rejected Joe's words: the touch stays planned and stops waiting on a draft. */
  withdrawDraft: (locationId: string, touchId: string) =>
    apiClient.post<ApiResponse<{ touchId: string }>>(`/community/touches/${touchId}/draft/withdraw`, { locationId }).then(() => undefined),
  logTouch: (locationId: string, partnerId: string, input: { activityType: string; activityOther?: string | null; date: string; notes?: string | null }) =>
    apiClient.post<ApiResponse<{ id: string }>>(`/community/partners/${partnerId}/touches`, { locationId, ...input }).then(unwrap),
  planTouch: (locationId: string, input: PlanTouchInput) =>
    apiClient.post<ApiResponse<{ partnerId: string; touchId: string; draftRequested: boolean }>>('/community/touches', { locationId, ...input }).then(unwrap),
  setTouchStatus: (locationId: string, touchId: string, status: 'done' | 'skipped', date?: string) =>
    apiClient.patch<ApiResponse<{ id: string }>>(`/community/touches/${touchId}`, { locationId, status, ...(date ? { date } : {}) }).then(unwrap),
  updateSettings: (locationId: string, patch: { radiusMi?: number; enabledCategories?: string[]; enabledEventTypes?: string[]; referralCheckinOn?: boolean }) =>
    apiClient
      .patch<ApiResponse<{ radiusMi: number; enabledCategories: string[]; enabledEventTypes: string[]; referralCheckinOn: boolean }>>('/community/settings', { locationId, ...patch })
      .then(unwrap),
  getReferralRows: (locationId: string, month: string) =>
    apiClient.get<ApiResponse<ReferralRow[]>>('/community/referrals/rows', { params: { locationId, month } }).then(unwrap),
  confirmReferrals: (locationId: string, month: string, rows: Array<{ partnerId: string; count: number | null }>) =>
    apiClient.post<ApiResponse<Array<{ partnerId: string; month: string; count: number | null }>>>('/community/referrals/confirm', { locationId, month, rows }).then(unwrap),
  skipReferralMonth: (locationId: string, month: string) =>
    apiClient.post<ApiResponse<{ skipped: number }>>('/community/referrals/skip', { locationId, month }).then(unwrap),
  getCadence: (locationId: string, partnerId: string) =>
    apiClient.get<ApiResponse<CadenceDue | null>>(`/community/cadence/${partnerId}`, { params: { locationId } }).then(unwrap),
  approveCadence: (locationId: string, partnerId: string, input: { activityType?: string; date?: string; rhythm?: string; rhythmDays?: number | null; draftRequested?: boolean }) =>
    apiClient.post<ApiResponse<{ touchId: string; draftRequested: boolean }>>(`/community/cadence/${partnerId}/approve`, { locationId, ...input }).then(unwrap),
  /** Idempotent: the Tasks panel calls it when it shows a draft task that names its touch. */
  linkDraftTask: (locationId: string, touchId: string, taskId: string) =>
    apiClient.post<ApiResponse<{ touchId: string; draftTaskId: string | null }>>(`/community/touches/${touchId}/draft-task`, { locationId, taskId }).then(unwrap),
  skipCadence: (locationId: string, partnerId: string) =>
    apiClient.post<ApiResponse<{ skipped: boolean }>>(`/community/cadence/${partnerId}/skip`, { locationId }).then(unwrap),
};
